import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";

const colorFor = (s) => s === "online" ? "#34C759" : s === "degraded" ? "#FFCC00" : "#FF3B30";

export default function ClientMap() {
  const [subs, setSubs] = useState([]);
  const [olts, setOlts] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([http.get("/subscribers"), http.get("/olts")]);
      setSubs(a.data); setOlts(b.data);
    })();
  }, []);

  const filtered = filter === "all" ? subs : subs.filter((s) => s.connection_status === filter);

  const center = useMemo(() => {
    if (!subs.length) return [12.9716, 77.5946];
    return [
      subs.reduce((s, x) => s + x.latitude, 0) / subs.length,
      subs.reduce((s, x) => s + x.longitude, 0) / subs.length,
    ];
  }, [subs]);

  const counts = useMemo(() => ({
    online: subs.filter((s) => s.connection_status === "online").length,
    degraded: subs.filter((s) => s.connection_status === "degraded").length,
    offline: subs.filter((s) => s.connection_status === "offline").length,
  }), [subs]);

  return (
    <div className="space-y-6 fade-up" data-testid="map-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Geo-view</div>
          <h1 className="font-display font-bold text-4xl mt-1">Client location map</h1>
        </div>
        <div className="flex items-center gap-2">
          {[
            { k: "all", label: "All", n: subs.length },
            { k: "online", label: "Online", n: counts.online },
            { k: "degraded", label: "Degraded", n: counts.degraded },
            { k: "offline", label: "Offline", n: counts.offline },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`map-filter-${f.k}`}
              className={`text-xs uppercase tracking-wide px-3 py-1.5 border rounded-sm inline-flex items-center gap-2 transition-colors ${filter === f.k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              {f.k !== "all" && <span className={`pulse-dot ${f.k}`} />}{f.label} <span className="font-mono opacity-70">{f.n}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="h-[560px] dark-tiles">
          <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            {olts.map((o) => (
              <CircleMarker
                key={o.id}
                center={[o.latitude, o.longitude]}
                radius={9}
                pathOptions={{ color: "#007AFF", weight: 2, fillColor: "#007AFF", fillOpacity: 0.25 }}
              >
                <Popup>
                  <div className="text-xs"><b>{o.name}</b><br />{o.vendor} · {o.model}<br />{o.ip_address}<br />{o.location}</div>
                </Popup>
              </CircleMarker>
            ))}
            {filtered.map((s) => (
              <CircleMarker
                key={s.id}
                center={[s.latitude, s.longitude]}
                radius={5}
                pathOptions={{ color: colorFor(s.connection_status), fillColor: colorFor(s.connection_status), fillOpacity: 0.9 }}
              >
                <Popup>
                  <div className="text-xs" style={{ minWidth: 200 }}>
                    <b>{s.full_name}</b><br />
                    <span className="font-mono">{s.username}</span> · {s.connection_status}<br />
                    <span className="font-mono">{s.ip_address}</span><br />
                    {s.address}<br />
                    Signal: <b>{s.signal_dbm} dBm</b>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
